import { z } from 'zod';

/**
 * Contrato de `GET /api/banners` (facturacion-backend, portalBanner.controller).
 *
 * Se valida con zod al recibir en vez de confiar en el tipo: el contenido nace
 * de un JSON en Azure Blob que hoy se edita A MANO, así que un banner mal
 * formado no es un caso hipotético. Con el parseo, uno roto se descarta solo y
 * los demás se siguen viendo; sin él, un `contentHtml` ausente revienta el
 * render y se lleva por delante el dashboard entero.
 */
/**
 * Cómo se compone el banner. El servidor decide el default (`alerta`) para los
 * registros viejos, así que aquí nunca llega vacío.
 */
export const bannerLayoutSchema = z.enum(['alerta', 'destacado', 'html']);
export type BannerLayout = z.infer<typeof bannerLayoutSchema>;

export const bannerCtaSchema = z.object({
  texto: z.string(),
  destino: z.string(),
});

export const portalBannerSchema = z.object({
  id: z.string().min(1),
  type: z.string().default('info'),
  layout: bannerLayoutSchema.default('alerta'),
  /** Ya saneado por lista blanca en el servidor — ver portalBanner.controller. */
  contentHtml: z.string(),
  priority: z.number().default(0),
  titulo: z.string().optional(),
  imagen: z.string().optional(),
  cta: bannerCtaSchema.optional(),
});

export const portalBannersResponseSchema = z.object({
  banners: z.array(portalBannerSchema),
});

export type PortalBanner = z.infer<typeof portalBannerSchema>;

/**
 * `bannerAlertType` del JSON → variante visual del portal.
 *
 * El tipo es texto libre en el storage (lo escribe quien administra), así que
 * cualquier valor no contemplado cae en `info` en vez de quedarse sin estilo.
 */
export function varianteDe(type: string): 'info' | 'warning' | 'danger' | 'success' {
  switch (type.trim().toLowerCase()) {
    case 'warning':
    case 'alerta':
    case 'aviso':
      return 'warning';
    case 'danger':
    case 'error':
    case 'critico':
      return 'danger';
    case 'promo':
    case 'promocion':
    case 'success':
      return 'success';
    default:
      return 'info';
  }
}

// ---------------------------------------------------------------------------
// Panel de administración (`/api/banners/admin`, solo SUPER ADMIN)
// ---------------------------------------------------------------------------

/** Los 7 valores de segmentación por rol, con su etiqueta para el formulario. */
export const ROLES_BANNER = [
  { value: 'SuperAdmin', label: 'Super Admin' },
  { value: 'Admin', label: 'Administrator' },
  { value: 'CompanyAdmin', label: 'Company Admin / NAOS Administrator' },
  { value: 'Vendedor', label: 'Vendedor' },
  { value: 'B2b', label: 'Customer B2B' },
  { value: 'Consult', label: 'Invoices Consult' },
  { value: 'InternalAdmin', label: 'Internal Admin' },
] as const;

/**
 * Qué portal sirve el banner: el legado ASP.NET (`AludraPortalEInvoice`) o
 * este portal nuevo React. Ambos leen el mismo JSON de storage — sin este
 * campo, un banner se ve en los dos sin forma de limitarlo a uno solo.
 * `target.portals: []` = ambos.
 */
export const PORTALES_BANNER = [
  { value: 'legado', label: 'Portal legado (ASP.NET)' },
  { value: 'nuevo', label: 'Portal nuevo (este)' },
] as const;

export const TIPOS_BANNER = [
  { value: 'info', label: 'Información' },
  { value: 'warning', label: 'Aviso' },
  { value: 'promo', label: 'Promoción' },
  { value: 'danger', label: 'Crítico' },
] as const;

export const LAYOUTS_BANNER = [
  { value: 'alerta', label: 'Alerta', ayuda: 'Franja compacta con icono. Para avisos breves.' },
  { value: 'destacado', label: 'Destacado', ayuda: 'Tarjeta con título, imagen y botón. Modo oscuro y responsive incluidos.' },
  { value: 'html', label: 'HTML libre', ayuda: 'Lienzo completo. Solo estilos en línea: sin responsive ni modo oscuro.' },
] as const;

/** El registro tal cual vive en el JSON de storage. */
export const bannerAdminSchema = z.object({
  bannerAlertId: z.string(),
  isActive: z.boolean(),
  priority: z.number(),
  startDate: z.string(),
  endDate: z.string(),
  target: z.object({
    tenants: z.array(z.string()).default([]),
    companies: z.array(z.string()).default([]),
    roles: z.array(z.string()).default([]),
    // Registros previos a este campo no lo traen — [] preserva su
    // comportamiento actual (se ven en ambos portales).
    portals: z.array(z.string()).default([]),
  }),
  bannerAlertType: z.string(),
  content: z.string(),
  layout: bannerLayoutSchema.default('alerta'),
  titulo: z.string().optional(),
  imagen: z.string().optional(),
  cta: bannerCtaSchema.optional(),
  createdDate: z.string().optional(),
  createdBy: z.string().optional(),
  lastModifiedDate: z.string().optional(),
  lastModifiedBy: z.string().optional(),
});

export const bannerAdminListSchema = z.object({
  version: z.number(),
  updatedAt: z.string(),
  updatedBy: z.string(),
  banners: z.array(bannerAdminSchema),
});

export type BannerAdmin = z.infer<typeof bannerAdminSchema>;
export type BannerAdminList = z.infer<typeof bannerAdminListSchema>;

/** Lo que manda el formulario. Sin `bannerAlertId`: lo asigna el servidor. */
export type BannerFormulario = Omit<
  BannerAdmin,
  'bannerAlertId' | 'createdDate' | 'createdBy' | 'lastModifiedDate' | 'lastModifiedBy'
>;

/**
 * Estado de vigencia, para la etiqueta del listado.
 *
 * Se calcula en el cliente solo para MOSTRARLO. Quién ve realmente el banner lo
 * decide el servidor: aquí un reloj mal puesto solo pinta una etiqueta rara.
 */
export function estadoDe(b: BannerAdmin): 'apagado' | 'programado' | 'vigente' | 'vencido' {
  if (!b.isActive) return 'apagado';
  const ahora = Date.now();
  const inicio = Date.parse(b.startDate);
  const fin = Date.parse(b.endDate);
  if (Number.isFinite(inicio) && ahora < inicio) return 'programado';
  if (Number.isFinite(fin) && ahora > fin) return 'vencido';
  return 'vigente';
}
